import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { IOverlayContext, IOverlayProvider } from './interfaces';

const OverlayContext = createContext<IOverlayContext | null>(null);

export const useOverlayContext = () =>
  useContext(OverlayContext) as IOverlayContext;

const OverlayProvider = ({ children }: IOverlayProvider) => {
  const [currentlyOpenOverlays, setCurrentlyOpenOverlays] = useState<string[]>(
    [],
  );
  const [hideCurrentlyOpenOverlays, setHideCurrentlyOpenOverlays] =
    useState<boolean>(false);

  const addToCurrentlyOpenOverlays = useCallback((overlayName: string) => {
    setCurrentlyOpenOverlays((prev) => {
      if (prev.includes(overlayName)) {
        return prev;
      }
      return [...prev, overlayName];
    });
  }, []);

  const removeFromCurrentlyOpenOverlays = useCallback((overlayName: string) => {
    setCurrentlyOpenOverlays((prev) => {
      if (!prev.includes(overlayName)) {
        return prev;
      }

      return prev.filter((name) => name !== overlayName);
    });
  }, []);

  const isOverlayCurrentlyOpen = useCallback(
    (overlayName: string) => {
      return currentlyOpenOverlays.includes(overlayName);
    },
    [currentlyOpenOverlays],
  );

  const contextValue = useMemo(
    () => ({
      addToCurrentlyOpenOverlays,
      removeFromCurrentlyOpenOverlays,
      isOverlayCurrentlyOpen,
      hideCurrentlyOpenOverlays,
      setHideCurrentlyOpenOverlays,
    }),
    [
      addToCurrentlyOpenOverlays,
      removeFromCurrentlyOpenOverlays,
      isOverlayCurrentlyOpen,
      hideCurrentlyOpenOverlays,
      setHideCurrentlyOpenOverlays,
    ],
  );

  return (
    <OverlayContext.Provider value={contextValue}>
      {children}
    </OverlayContext.Provider>
  );
};

export default OverlayProvider;
